/* eslint-disable @typescript-eslint/no-explicit-any */

import type { Draft} from "immer";
import { produce } from "immer";

/**
 * Traverse an object to get the value at the specified path.
 * Handles both object keys and array indices in the path.
 * @param obj - The object to traverse.
 * @param path - The dot-separated path, e.g., "a.b.c.5.d".
 * @returns The value at the specified path, or undefined if not found.
 */
function getFormValueAtPath<
    FormData extends Record<string, unknown>,
    Path extends string, // DeepKeysOf<FormData> = DeepKeysOf<FormData>,
    FieldValue extends unknown | undefined | null = unknown,
    // | DeepPathValueType<FormData, Path>
    // | undefined
    // | null = DeepPathValueType<FormData, Path> | undefined,
>(obj: Partial<FormData>, path: Path): FieldValue {
    const keys = path.split(".");
    let current: any = obj;

    for (const key of keys) {
        if (current === null || current === undefined) {
            // Return undefined if the current value is null/undefined
            return undefined as FieldValue;
        }

        if (Array.isArray(current)) {
            // If the current value is an array, parse the key as an index
            const index = parseInt(key, 10);
            if (isNaN(index)) {
                throw new Error(`Invalid array index: ${key}`);
            }
            current = current[index];
        } else {
            // Otherwise, treat the key as a property
            current = current[key];
        }
    }

    return current as FieldValue;
}

/**
 * Set a value at the specified path.
 * Handles both object keys and array indices in the path.
 * @param obj - The original object.
 * @param path - The dot-separated path, e.g., "a.b.c.5.d".
 * @param value - The value to set at the specified path.
 * @returns A new object with the value set at the specified path.
 */
function setFormValueAtPath<
    FormData extends Record<string, unknown>,
    Path extends string, // DeepKeysOf<FormData>,
    FieldValue,
>(obj: Partial<FormData>, path: Path, value: FieldValue): Partial<FormData> {
    const keys = path.split(".");

    let current: any = obj;

    for (let i = 0; i < keys.length; i++) {
        const key = keys[i];

        if (i === keys.length - 1) {
            // Final key, set the value
            if (Array.isArray(current)) {
                const index = parseInt(key, 10);
                if (isNaN(index)) {
                    throw new Error(`Invalid array index: ${key}`);
                }
                current[index] = value;
            } else {
                current[key] = value;
            }
            break;
        }

        if (Array.isArray(current)) {
            // If the current value is an array, parse the key as an index
            const index = parseInt(key, 10);
            if (isNaN(index)) {
                throw new Error(`Invalid array index: ${key}`);
            }
            // Ensure the array is large enough
            while (current.length <= index) {
                current.push(undefined);
            }
            // Move to the next level
            if (current[index] === undefined) {
                current[index] = {}; // Create an empty object if undefined
            }
            current = current[index];
        } else {
            // If the current value is not an array, treat the key as a property
            if (!(key in current)) {
                current[key] = {}; // Create an empty object if the key doesn't exist
            }
            current = current[key];
        }
    }
    return obj;
}
/**
 * Set a value at the specified path immutably using Immer.
 * Handles both object keys and array indices in the path.
 * @param obj - The original object.
 * @param path - The dot-separated path, e.g., "a.b.c.5.d".
 * @param value - The value to set at the specified path.
 * @returns A new object with the value set at the specified path.
 */
function setFormValueAtPathImmutable<
    FormData extends Record<string, unknown>,
    Path extends string, // DeepKeysOf<FormData>,
    FieldValue,
>(obj: Partial<FormData>, path: Path, value: FieldValue): Partial<FormData> {
    return produce(obj, (draft: Draft<FormData>) => {
        setFormValueAtPath(draft, path, value);
    });
}

export { getFormValueAtPath, setFormValueAtPath, setFormValueAtPathImmutable };
